import "./RegisterPage.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import RegStepFields from "./RegStepFields";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {env} from "../../env/env";
import RegStepConfirm from "./RegStepConfirm";
import {useParams} from "react-router";
import {DashboardDtoStripePlanTypeEnum, RegMemberDto} from "../../generated/dashboard-client";
import {logSignIn, logSignUp} from "../../service/AnalyticsService";
import {generatePassword, getRestaurantTypeLabel, iniFrame, setSiteBannerCookie} from "../../util/utils";
import MembersApi from "../../api/MembersApi";
import RegStepZero, {RegOrLoginDecision} from "./RegStepZero";
import LoginPageStep from "./LoginPageStep";
import Storage from "../../util/Storage";
import AppApi from "../../api/AppApi";
import {handleServerError} from "../../util/ErrorHandler";
import RegStepPopmenuAuth from "./RegStepPopmenuAuth";
import RegStepPopmenuLocation from "./RegStepPopmenuLocation";
import {RegPageNavProps} from "../../util/types";
import DashboardApi from "../../api/DashboardApi";
import RegisterPageLeft from "./RegisterPageLeft";
import RegStepPlan from "./RegStepPlan";
import RegStepPayment from "./RegStepPayment";
import {drdFlowState, memberState, planTypeState, popmenuFlowState, regStepState} from "../../state/atoms";
import {useRecoilState} from "recoil";
import RegStepAbout from "./RegStepAbout";
import CancelRegistrationModal from "./CancelRegistrationModal";
import RegStepMicrosoftWelcome from "./RegStepMicrosoftWelcome";
import UsersApi from "../../api/UsersApi";
import {SsoMember, RegisterMemberDto} from "../../generated/dashboard-client";
import {BUSINESS_TYPE_MULTIPLE, TOTAL_REG_STEPS} from "../../util/constants";
import PosApi from "../../api/PosApi";
import {logout} from "../../service/AuthService";

export enum RegStep {
  Login,
  LoginUser,
  LoginPassword,
  Fields,
  About,
  PlanSetup,
  Payment,
  Confirm,
  PopmenuAuth,
  PopmenuLocation,
  MicrosoftWelcome
}

interface LoginProps {
  onLogin?: () => void;
}

// Extracts the invite id from a path like /account/register/c2QSBLqXkw. Returns undefined for the
// owner paths (/account/login, /account/register) which have no trailing id.
function inviteIdFromPath(path?: string): string | undefined {
  if (!path) return undefined;
  return path.match(/\/account\/register\/([^/?#]+)/)?.[1];
}

// Parses the DiningRD context carried through the Microsoft redirect (loginWithMicrosoft passes
// /account/register?source=drd&community=...&corporate=...). Returns undefined when not a drd flow.
function drdContextFromPath(path?: string): { community: string; corporate: string } | undefined {
  if (!path || path.indexOf("?") === -1) return undefined;
  const params = new URLSearchParams(path.slice(path.indexOf("?")));
  if (params.get("source") !== "drd") return undefined;
  return { community: params.get("community") || "", corporate: params.get("corporate") || "" };
}

export interface ExtendedRegisterMemberDto extends RegisterMemberDto {
  confirm?: string;
  invitation?: string;
  businessType?: string;
  role?: string;
  business: string;
  phone: string;
  diffPhone?: string;
  zip: string;
  smsOptIn: boolean;
  pos?: string;
  omgr?: string;
  fsp?: string;
  dss?: string;
  needs: string[];
}

export default function RegisterPage({onLogin}: LoginProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const {inviteId} = useParams<{ inviteId: string }>();

  const [step, setStep] = useState<RegStep>(RegStep.Login);
  const [next, setNext] = useState(null as string | null);
  const [, setMemberHasRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [googleRegister, setGoogleRegister] = useState(false);
  const [microsoftRegister, setMicrosoftRegister] = useState(false);
  const [decision, setDecision] = useState({} as RegOrLoginDecision);
  const [regStepTotal, setRegStepTotal] = useState(TOTAL_REG_STEPS);
  const state = location.state || {};
  const [error, setError] = useState("");
  const [microsoftInviteError, setMicrosoftInviteError] = useState("");
  const [microsoftOrg, setMicrosoftOrg] = useState("");
  const microsoftInviteProcessed = useRef(false);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [drdCommunity, setDrdCommunity] = useState("");
  const [drdCorporateGroupName, setDrdCorporateGroupName] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [diffPhone, setDiffPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [businessType, setBusinessType] = useState(null as string | null);
  const [role, setRole] = useState(null as string | null);
  const [pos, setPos] = useState(null as string | null);
  const [omgr, setOmgr] = useState(null as string | null);
  const [fsp, setFsp] = useState(null as string | null);
  const [dss, setDss] = useState(null as string | null);
  const [needs, setNeeds] = useState([] as string[]);

  const [planType, setPlanType] = useRecoilState(planTypeState);
  const [regStep, setRegStep] = useRecoilState(regStepState);
  const [popmenuFlow, setPopmenuFlow] = useRecoilState(popmenuFlowState);
  const [drdFlow, setDrdFlow] = useRecoilState(drdFlowState);
  const [member, setMember] = useRecoilState(memberState);
  const [fromRegister, setFromRegister] = useState(false);

  useEffect(() => {
    setFromRegister(location.pathname.includes('register') || ![RegStep.Login, RegStep.LoginUser, RegStep.LoginPassword].includes(step));
  }, [step, location.pathname])

  useEffect(() => {
    if (!searchParams) return;
    const source = searchParams.get("source");
    if (source && source === "popmenu") {
      setPopmenuFlow(true);
    }
    if (source && source === "drd") {
      setDrdFlow(true);
    }

    const pt = searchParams.get("plan");
    if (pt && (pt === "pro" || pt === "design")) {
      setPlanType(pt);
    }
  }, [searchParams, setPlanType, setPopmenuFlow, setDrdFlow]);

  useEffect(() => {
    if (!state.email) return;
    setEmail(state.email);
  }, [state.email]);

  useEffect(() => {
    if (!state.step) return;
    if (state.step === "pass") {
      setStep(RegStep.LoginPassword);
    }
    if (state.step === "user") {
      setStep(RegStep.LoginUser);
    }
  }, [state.step]);

  useEffect(() => {
    if (!loading) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading]);

  useEffect(() => {
    if (!inviteId) return;

    let cb = Promise.resolve();
    if (Storage.getToken()) {
      cb = new Promise(resolve => {
        logout().then(() => {
          resolve();
        });
      });
    }

    cb.then(() => {
      if (inviteId) {
        Storage.removeRegisterFlag();
      }
      setLoading(true);
      UsersApi.getUserInvite(inviteId)
        .then((resp) => {
          // Invites from an enterprise SSO owner's team must go through that provider's SSO page,
          // not the email registration form. (ssoProvider isn't in the generated client type yet.)
          const ssoProvider = (resp.data as any).ssoProvider as string | undefined;
          if (ssoProvider) {
            navigate(`/account/sso/${ssoProvider}`, {replace: true});
            return;
          }
          setLoading(false);
          if (resp.data.email) {
            setEmail(resp.data.email);
            setInvitedEmail(resp.data.email);
          }
        })
        .catch((error) => {
          setLoading(false);
          if (error?.response?.data?.message === "invitation.expired") {
            setLinkExpired(true);
          }
        });
    });
  }, [inviteId, navigate]);

  // When we return to the invite URL from /account/login carrying an authenticated Microsoft member
  // (see onDecision), complete the invite once the invitation's email has loaded. Runs once.
  useEffect(() => {
    const st = location.state as any;
    const msMember = st?.microsoftMember as SsoMember | undefined;
    if (!msMember || !inviteId || !invitedEmail || microsoftInviteProcessed.current) return;
    microsoftInviteProcessed.current = true;
    const domainLabel = (msMember.email || "").split("@")[1]?.split(".")[0] || "";
    setMicrosoftOrg(st?.microsoftOrg || domainLabel);
    onMicrosoftInviteSuccess(msMember);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, inviteId, invitedEmail]);

  useEffect(() => {
    if (Storage.getToken()) {
      if ((location.state as RegPageNavProps)?.resumePopmenuFlow) {
        setPopmenuFlow(true);
      } else if (Storage.getRegisterFlag() !== 'true') {
        onLogin?.();
        navigate(env.dashboardPath);
      }
    }
  }, [navigate, onLogin, location.state, setPopmenuFlow]);

  useEffect(() => {
    if (state?.step || !location) return;

    if ((location.state as RegPageNavProps)?.resumePopmenuFlow) {
      setStep(RegStep.PopmenuLocation);
    } else {
      const redirectToPlans = Storage.getRegisterFlag() === 'true' && !inviteId && !!Storage.getToken();
      if (redirectToPlans) {
        setMemberHasRegistered(true);
      }
      let s = RegStep.Login;
      if (inviteId) s = RegStep.Fields;
      if (redirectToPlans) s = RegStep.PlanSetup;
      if (drdFlow) s = RegStep.Fields;
      setStep(s);

      let total = TOTAL_REG_STEPS
      if (inviteId) total = 2;
      if (popmenuFlow) total = 3;
      if (drdFlow) total = 0;
      setRegStepTotal(total);
    }
  }, [inviteId, state.step, location, popmenuFlow, drdFlow]);

  useEffect(() => {
    if (popmenuFlow) {
      setPlanType('pro');
    }
    if (drdFlow) {
      setPlanType('design');
    }
  }, [popmenuFlow, setPlanType, drdFlow]);

  useEffect(() => {
    switch (step) {
      case RegStep.Fields:
        setRegStep(1);
        break;
      case RegStep.About:
        setRegStep(2);
        break;
      case RegStep.PlanSetup:
        setRegStep(3);
        break;
      case RegStep.Payment:
        if (popmenuFlow) {
          setRegStep(3);
        } else {
          setRegStep(4);
        }
        break;
      default:
        setRegStep(0);
        break;
    }
  }, [step, setRegStep, popmenuFlow]);

  function openCancelModal() {
    setCancelModalOpen(true);
  }

  function cancelRegistration() {
    setCancelModalOpen(false);
    logout();
    Storage.removeRegisterFlag();

    setEmail("");
    setPassword("");
    setFirst("");
    setLast("");
    setZip("");
    setPhone("");
    setDiffPhone("");
    setBusiness("");
    setBusinessType(null);
    setRole(null);
    setPos(null);
    setOmgr(null);
    setFsp(null);
    setDss(null);
    setNeeds([]);
    setDrdCommunity("");
    setDrdCorporateGroupName("");
    setSmsOptIn(false);
    setGoogleRegister(false);
    setMicrosoftRegister(false);
    setMemberHasRegistered(false);
    setError("");
    setPopmenuFlow(false);
    setDrdFlow(false);

    setStep(RegStep.LoginUser);
    navigate("/account/login", {state: {step: "user"}, replace: true});
  }

  function toDashboard() {
    if (next) {
      if (next === "upgrade") {
        navigate(env.dashboardPath, {state: {showUpgradeModal: true}});
      } else {
        window.location.href = next;
      }
    } else {
      if (popmenuFlow) {
        window.location.href = env.dashboardPath + "?popmenuWelcome=true";
      } else {
        onLogin?.();
        navigate(env.dashboardPath);
      }
    }
  }

  function onRegSuccess(member: RegMemberDto, internalId: number = 0) {
    if (!inviteId) {
      logSignUp(internalId, businessType || '');

      if (businessType === BUSINESS_TYPE_MULTIPLE) {
        MembersApi.sendMultiRegLead(member);
      }

      if (iniFrame()) {
        window.location.href = `${env.basePath}${env.timberContextPath}/?size=digital-menu-board`;
      } else {
        if (popmenuFlow) {
          setStep(RegStep.Payment);
        } else {
          if (!planType) {
            setStep(RegStep.PlanSetup);
          } else {
            setStep(RegStep.Payment);
          }
        }
      }
      return;
    }

    if (next) {
      if (next === "upgrade") {
        navigate(env.dashboardPath, {state: {showUpgradeModal: true}});
      } else {
        window.location.href = next;
      }
    } else {
      navigate(env.dashboardPath);
    }
  }

  async function onMicrosoftInviteSuccess(msMember: SsoMember) {
    setMicrosoftInviteError("");

    if (!inviteId) return;

    const msEmail = (msMember?.email || "").trim().toLowerCase();
    const expected = (invitedEmail || "").trim().toLowerCase();

    if (!expected) {
      setMicrosoftInviteError("Unable to verify the invitation. Please reload the page and try again.");
      return;
    }

    if (msEmail !== expected) {
      setMicrosoftInviteError(`This invitation was sent to ${invitedEmail}. Please sign in with the correct Microsoft account.`);
      return;
    }

    setLoading(true);
    try {
      if (msMember.exists) {
        setLoading(false);
        setMicrosoftInviteError("This email is already in use.");
        return;
      }

      const ssoPassword = msMember.ssoId ?? "";
      await UsersApi.register({
        first: msMember.firstName || "",
        last: msMember.lastName || "",
        password: ssoPassword,
        confirm: ssoPassword,
        invitation: inviteId,
        ssoMicrosoft: true
      });

      const loginResp = await AppApi.login({username: msMember.email!, password: ssoPassword});
      if (loginResp.data.token) {
        Storage.setToken(loginResp.data.token);
        logSignIn(loginResp.data.mid + "");
        setMemberHasRegistered(true);
        setFirst(msMember.firstName || "");
        setLoading(false);
        // Team users get the welcome screen after joining; "Continue" routes on to the dashboard.
        setStep(RegStep.MicrosoftWelcome);
      } else {
        setLoading(false);
        setMicrosoftInviteError("We couldn't sign you in. Please try again.");
      }
    } catch (err: any) {
      setLoading(false);
      const message = err?.response?.data?.message || "";
      if (message === "This email is already in use." || message === "EmailInUse") {
        setMicrosoftInviteError("This email is already in use.");
      } else {
        setMicrosoftInviteError(message || "Unable to complete Microsoft sign-in. Please try again.");
      }
    }
  }

  // New DiningRD owner via Microsoft. Registers directly with source="drd" (backend creates it Active
  // with DiningRD integration and a location, no Stripe), logs in, and shows Confirm — mirroring the
  // email drd flow's outcome without the multi-step form.
  async function registerMicrosoftDrd(msMember: SsoMember | undefined, community: string, corporate: string) {
    setLoading(true);
    try {
      const password = generatePassword();
      const dto: ExtendedRegisterMemberDto = {
        first: msMember?.firstName || "",
        last: msMember?.lastName || "",
        email: msMember?.email || "",
        password,
        confirm: password,
        business: community.trim() || "DiningRD",
        drdCommunity: community.trim(),
        drdCorporateGroupName: corporate.trim(),
        source: "drd",
        restaurantType: "",
        numLocations: -1,
        ssoMicrosoft: true,
        phone: "",
        zip: "",
        smsOptIn: false,
        needs: [],
      };
      const resp = await MembersApi.registerMember(dto);
      if (typeof window.rewardful !== "undefined") {
        window.rewardful("convert", {email: dto.email});
      }
      const loginResp = await AppApi.login({username: dto.email!, password});
      if (loginResp.data.token) {
        Storage.setToken(loginResp.data.token);
        logSignUp(resp.data.internalId || 0, "");
        logSignIn(loginResp.data.mid + "");
        Storage.removeRegisterFlag();
        setStep(RegStep.Confirm);
      } else {
        setError("We couldn't complete your sign-in. Please try again.");
      }
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  }

  function onDecision(decision: RegOrLoginDecision) {
    setDecision(decision);

    if (decision.type === "microsoft") {
      // If the sign-in started from an invite URL, return to it (carrying the already-authenticated
      // member so we don't re-trigger the redirect) and complete the invite there.
      const inviteId = inviteIdFromPath(decision.returnPath);
      if (inviteId) {
        navigate(decision.returnPath!, {state: {microsoftMember: decision.gMember, microsoftOrg: decision.orgName}});
        return;
      }

      // DiningRD signup: register directly as a drd member (status Active, DiningRD integration) and
      // go to Confirm — no About/Plan/Payment. An existing drd account just logs in via the owner path.
      const drd = drdContextFromPath(decision.returnPath);
      if (drd && !decision.gMember?.exists) {
        registerMicrosoftDrd(decision.gMember, drd.community, drd.corporate);
        return;
      }

      // Owner sign-in/up — mirror the Google flow: an existing account logs straight in with its
      // stored credential; a new account goes through About (business info) → PlanSetup → Payment.
      setMicrosoftRegister(true);
      if (decision.gMember?.exists) {
        setLoading(true);
        setEmail(decision.gMember.email!);
        performLoginPublic(decision.gMember.email!, (decision.gMember.token || decision.gMember.ssoId)!).then();
      } else {
        setFirst(decision.gMember?.firstName || "");
        setLast(decision.gMember?.lastName || "");
        setEmail(decision.gMember?.email || "");
        setStep(RegStep.About);
        setLoading(false);
      }
    } else if (decision.type === "google") {
      setGoogleRegister(true);

      if (decision.gMember?.id) {
        setLoading(true);
        setEmail(decision.gMember.email!);
        setPassword(decision.gMember.token!);
        performLoginPublic(decision.gMember?.email!, decision.gMember?.token!).then();
      } else {
        setStep(RegStep.About);
        setLoading(false);
      }
    } else {
      setEmail("");
      setPassword("");
      setError("");
      setStep(RegStep.LoginUser);
    }
  }

  const performLoginPublic = useCallback(async (email: string, password: string, handoffCode?: string) => {
    if (!handoffCode && (!password || !email)) return;
    setLoading(true);
    try {
      const loginResp = handoffCode
        ? await AppApi.loginHandoff(handoffCode)
        : await AppApi.login({username: email, password: password.trim()});
      const resp = loginResp.data;

      if (resp.error) {
        let errMsg: any = "We couldn't find that login / password combination.";
        if ("paused" === resp.code) {
          errMsg = "This account is paused. Please contact your account owner.";
        } else if ("canceled" === resp.code) {
          errMsg = "This account is cancelled. Please contact your account owner.";
        } else if ("declined" === resp.code) {
          errMsg = "This account is declined. Please contact your account owner.";
        } else if ("portal" === resp.code) {
          errMsg = "This account can only sign in to the partner portal.";
        } else if ("blocked" === resp.code) {
          errMsg = <div style={{textAlign: "center"}}>
            <b>This account is blocked.</b><br/>
            Please contact our customer service team.
          </div>;
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      if (resp.token) {
        Storage.setToken(resp.token);
      }

      logSignIn(resp.mid + "");
      setSiteBannerCookie(true);
      try {
        await MembersApi.increaseMemberLoginCount(resp.mid!);
      } catch (e) {
        console.error(e);
      }

      if (next) {
        window.location.href = next;
      } else {
        if (!popmenuFlow) {
          onLogin?.();
        }

        if (iniFrame()) {
          window.location.href = `${env.basePath}${env.timberContextPath}/?size=digital-menu-board`;
        } else {
          setLoading(true);
          const statusResponse = await DashboardApi.status();
          setLoading(false);

          const {member, locations} = statusResponse.data;

          if (popmenuFlow) {
            if (member?.status === 5) {
              setStep(RegStep.Payment);
            } else if (member?.status === 1) {
              setLoading(true);
              PosApi.getPopmenuRestaurants().then(resp => {
                if (resp.data.length > 0) {
                  onLogin?.();
                  navigate(env.dashboardPath);
                } else {
                  setStep(RegStep.PopmenuAuth);
                  setLoading(false);
                }
              });
            }
          } else if (drdFlow) {
            if (resp.mid) {
              MembersApi.updateMemberDiningRDFlag(resp.mid, true);
            }
            navigate(env.dashboardPath);
          } else {
            const firstLocationId = locations?.[0]?.internalId;
            if (member && member.permissions && firstLocationId && member.permissions[firstLocationId]?.itemLibraryOnly) {
              navigate(`${env.dashboardPath}/item-library`);
            } else {
              navigate(env.dashboardPath);
            }
          }
        }
      }

    } catch (err) {
      console.error(err);
      setError(handoffCode
        ? "This sign-in link has expired. Please open the account again from the partner portal."
        : "We couldn't find that login / password combination.");
    }

    setLoading(false);
  }, [navigate, next, onLogin, popmenuFlow, drdFlow]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    let toBeNext = null;
    if (query.get("next") !== null) {
      if (query.get("next")!.indexOf("pos-verification") > -1) {
        toBeNext = window.location.href.split("next=")[1];
      } else {
        toBeNext = query.get("next");
      }
    }
    setNext(toBeNext);

    if (query.get("tkn")) {
      Storage.setToken(query.get("tkn")!);
      setTimeout(() => navigate(env.dashboardPath), 3000);
    }

    if (query.get("e") && query.get("p")) {
      setEmail(query.get("e")!);
      setPassword(query.get("p")!);
      performLoginPublic(query.get("e")!, query.get("p")!).then();
    }

    const handoff = query.get("handoff");
    if (handoff) {
      window.history.replaceState(null, "", window.location.pathname);
      performLoginPublic("", "", handoff).then();
    }
  }, [navigate, performLoginPublic]);

  function onLoginUserNext() {
    setPassword("");
    setError("");

    setLoading(true);
    AppApi.checkEmail(email).then(resp => {
      setLoading(false);
      if (resp.data) {
        setStep(RegStep.LoginPassword);
      } else {
        setStep(RegStep.Fields);
      }
    });
  }

  function onLoginPasswordNext() {
    setLoading(true);
    AppApi.checkEmail(email).then(resp => {
      if (resp.data) {
        performLoginPublic(email, password).then();
      } else {
        setLoading(false);
        setStep(RegStep.Fields);
      }
    }).catch(e => {
      handleServerError(e);
      setLoading(false);
    });
  }

  function popmenuNext() {
    if (step === RegStep.PopmenuLocation) {
      setStep(RegStep.Confirm);
    } else {
      setStep(RegStep.PopmenuAuth);
    }
  }

  function paymentNext() {
    Storage.removeRegisterFlag();
    if (popmenuFlow) {
      setStep(RegStep.PopmenuAuth);
    } else {
      setStep(RegStep.Confirm);

      if (window.fbq && planType === 'design') {
        console.log("sending design plan startTrial to fb");
        window.fbq('track', 'StartTrial', {
          value: 0.00,
          currency: 'USD'
        });
      }

      setMember({
        ...member,
        stripePlanType: planType === 'pro' ? DashboardDtoStripePlanTypeEnum.Pro : DashboardDtoStripePlanTypeEnum.Design,
      });
    }
  }

  async function register(member: ExtendedRegisterMemberDto) {
    if (!!inviteId) {
      let user = buildUser(member);
      if (googleRegister) {
        user = buildSsoUser(member, decision.gMember!!);
      }
      await registerTeamUser(user);
    } else {
      let m = buildMember(member);
      if (googleRegister) {
        m = buildSsoMember(member, decision.gMember!!);
      } else if (microsoftRegister) {
        m = buildMicrosoftSsoMember(member, decision.gMember!!);
      }
      await registerAccount(m);
    }
  }

  async function registerTeamUser(user: ExtendedRegisterMemberDto) {
    setLoading(true);
    try {
      await UsersApi.register(user);
      setLoading(false);
      await performLoginSilent(email ?? "", user.password!.trim());
      setMemberHasRegistered(true);
    } catch (err: any) {
      setLoading(false);
      throw err;
    }
  }

  function reward() {
    if (typeof window.rewardful !== "undefined") {
      window.rewardful("convert", {email});
    }
  }

  function buildUser(member: ExtendedRegisterMemberDto): ExtendedRegisterMemberDto {
    return {
      ...member,
      first: member.first,
      last: member.last,
      password: member.password!.trim(),
      confirm: member.password!.trim(),
      drdCommunity: member.drdCommunity!.trim(),
      drdCorporateGroupName: member.drdCorporateGroupName!.trim(),
      invitation: inviteId
    };
  }

  function buildMember(member: ExtendedRegisterMemberDto): ExtendedRegisterMemberDto {
    return {
      ...member,
      referral: window.Rewardful?.referral,
      restaurantType: getRestaurantTypeLabel(businessType || ''),
      numLocations: -1,
      source: popmenuFlow ? "popmenu" : (drdFlow ? "drd" : "")
    };
  }

  function buildSsoMember(member: ExtendedRegisterMemberDto, gMember: SsoMember): ExtendedRegisterMemberDto {
    const password = generatePassword();
    return {
      ...member,
      first: gMember.firstName,
      last: gMember.lastName,
      email: gMember.email,
      password,
      drdCommunity,
      drdCorporateGroupName,
      referral: window.Rewardful?.referral,
      restaurantType: getRestaurantTypeLabel(businessType || ''),
      numLocations: -1,
      source: popmenuFlow ? "popmenu" : (drdFlow ? "drd" : "")
    };
  }

  // Same as the Google SSO member build (generated password, read back from the member on later
  // logins) plus the ssoMicrosoft flag so the backend records it as a Microsoft SSO account.
  function buildMicrosoftSsoMember(member: ExtendedRegisterMemberDto, gMember: SsoMember): ExtendedRegisterMemberDto {
    return {...buildSsoMember(member, gMember), ssoMicrosoft: true};
  }

  function buildSsoUser(member: ExtendedRegisterMemberDto, gMember: SsoMember): ExtendedRegisterMemberDto {
    const password = generatePassword();
    return {
      ...member,
      first: gMember.firstName,
      last: gMember.lastName,
      password,
      confirm: password,
      invitation: inviteId
    };
  }

  async function registerAccount(member: ExtendedRegisterMemberDto) {
    setLoading(true);

    try {
      const resp = await MembersApi.registerMember(member)
      reward();
      const {id, internalId, name, email} = resp.data;

      // @ts-ignore
      window['FS'].identify(internalId, {
        email: email,
        displayName: name
      });

      await performLoginSilent(member.email!, member.password!.trim(), id, internalId, async () => {
        await MembersApi.saveQuestionnaire(member.email!, {
          diffPhone: member.diffPhone || "",
          smsOptIn: member.smsOptIn,
          businessType: member.businessType || '',
          role: member.role || '',
          omgr: member.omgr || '',
          pos: member.pos || '',
          dss: member.dss || '',
          fsp: member.fsp || '',
          needs: member.needs.join(',')
        });

        setMemberHasRegistered(true);
      });
    } catch (err: any) {
      setLoading(false);
      throw err;
    }
  }

  async function performLoginSilent(email: string, password: string = "", id: string = "", internalId: number = 0, fnc?: () => Promise<void>) {
    setLoading(true);
    const loginResp = await AppApi.login({username: email, password: password.trim()});
    const resp = loginResp.data;

    if (resp.token) {
      Storage.setToken(resp.token);

      if (fnc) {
        await fnc();
      }

      setLoading(false);

      onRegSuccess({
        name: `${first} ${last}`,
        id, email, business, zip, phone,
        restaurantType: getRestaurantTypeLabel(businessType || ''),
        numLocations: -1
      }, internalId);
    }
  }

  async function onRegisterFieldsNext(member: RegisterMemberDto) {
    setFirst(member.first!);
    setLast(member.last!);
    setEmail(member.email!);
    setPassword(member.password!);
    setDrdCommunity(member.drdCommunity!);
    setDrdCorporateGroupName(member.drdCorporateGroupName!);

    if (!!inviteId || drdFlow) {
      const dto = {
        ...member,
        businessType: "",
        role: "",
        business: "",
        phone: "",
        diffPhone: "",
        zip: "",
        smsOptIn: false,
        pos: "",
        omgr: "",
        fsp: "",
        dss: "",
        needs: []
      };
      if (drdFlow) {
        dto.business = member.drdCommunity || 'DiningRD';
      }
      await onRegisterAboutNext(dto);
    } else {
      setStep(RegStep.About);
    }
  }

  async function onRegisterAboutNext(member: ExtendedRegisterMemberDto) {
    setBusinessType(member.businessType || '');
    setRole(member.role || '');
    setBusiness(member.business!);
    setPhone(member.phone!);
    setDiffPhone(member.diffPhone!);
    setZip(member.zip!);
    setSmsOptIn(member.smsOptIn!);
    setPos(member.pos!);
    setOmgr(member.omgr!);
    setFsp(member.fsp!);
    setDss(member.dss!);
    setNeeds(member.needs!);

    if (first) member.first = first;
    if (last) member.last = last;
    if (email && !member.email) member.email = email;
    if (password) member.password = password;
    if (drdCommunity) {
      member.drdCommunity = drdCommunity;
      member.drdCorporateGroupName = drdCorporateGroupName;
    }

    try {
      if (!inviteId) {
        Storage.setRegisterFlag('true');
      }
      await register(member);

      if (!!inviteId) {
        setStep(RegStep.Confirm);
      } else {
        if (popmenuFlow) {
          setStep(RegStep.Payment);
        } else if (drdFlow) {
          setStep(RegStep.Confirm);
          Storage.removeRegisterFlag();
        } else {
          setStep(RegStep.PlanSetup);
        }
      }
    } catch (e: any) {
      try {
        if ('This email is already in use.' === e.response.data.message) {
          onDecision({type: 'email'});
          setEmail(member.email || '');
        }
      } catch (e1) {
        console.error(e1);
      }
      handleServerError(e);
    }
  }

  return (
    <div className={`reg-page`}>
      <div className={"left"}>
        <RegisterPageLeft register={fromRegister} step={step}/>
      </div>
      <div className={"right"}>
        {step === RegStep.Login &&
          <RegStepZero onDecision={onDecision} loading={loading} onStartLoading={() => setLoading(true)} error={error}
                       onError={(msg) => { setError(msg); setLoading(false); }}/>
        }
        {step === RegStep.LoginUser &&
          <LoginPageStep step={"email"} email={email} loading={loading} error={error} password={password}
                         onEmailChange={setEmail}
                         onPasswordChange={setPassword} onNext={onLoginUserNext} onBack={() => setStep(RegStep.Login)}/>
        }
        {step === RegStep.LoginPassword &&
          <LoginPageStep step={"password"} email={email} loading={loading} error={error} password={password}
                         onEmailChange={setEmail}
                         onPasswordChange={setPassword} onNext={onLoginPasswordNext}
                         onBack={() => setStep(RegStep.LoginUser)}/>
        }
        {step === RegStep.Fields &&
          <RegStepFields
            loading={loading}
            drdFlow={drdFlow}
            onCancelRegistration={openCancelModal}
            googleRegister={googleRegister}
            inviteMode={!!inviteId}
            linkExpired={linkExpired}
            regStep={regStep}
            regStepTotal={regStepTotal}
            onNext={onRegisterFieldsNext}
            goBack={openCancelModal}
            first={first}
            last={last}
            email={email}
            password={password}
            drdCommunity={drdCommunity}
            drdCorporateGroupName={drdCorporateGroupName}
            onMicrosoftInviteSuccess={onMicrosoftInviteSuccess}
            onMicrosoftError={(msg) => { setMicrosoftInviteError(msg); setLoading(false); }}
            onStartLoading={() => setLoading(true)}
            microsoftError={microsoftInviteError}
          />}
        {step === RegStep.About &&
          <RegStepAbout
            onBack={() => setStep(RegStep.Fields)}
            onCancelRegistration={openCancelModal}
            onNext={onRegisterAboutNext}
            loading={loading}
            regStep={regStep}
            regStepTotal={regStepTotal}
            businessTypeParam={businessType}
            roleParam={role}
            businessParam={business}
            phoneParam={phone}
            diffPhoneParam={diffPhone}
            zipParam={zip}
            smsOptInParam={smsOptIn}
            posParam={pos}
            omgrParam={omgr}
            fspParam={fsp}
            dssParam={dss}
            needsParam={needs}
            popmenu={popmenuFlow}
          />}
        {step === RegStep.Confirm &&
          <RegStepConfirm toDashboard={toDashboard}/>}
        {step === RegStep.PopmenuAuth &&
          <RegStepPopmenuAuth email={email}/>}
        {step === RegStep.PopmenuLocation &&
          <RegStepPopmenuLocation onNext={popmenuNext}/>}
        {step === RegStep.PlanSetup &&
          <RegStepPlan onNext={() => setStep(RegStep.Payment)}
                       onCancelRegistration={openCancelModal} regStep={regStep}
                       regStepTotal={regStepTotal}/>}
        {step === RegStep.Payment &&
          <RegStepPayment onNext={paymentNext} onBack={popmenuFlow ? undefined : () => setStep(RegStep.PlanSetup)}
                          onCancelRegistration={openCancelModal} regStep={regStep} regStepTotal={regStepTotal}/>}
        {step === RegStep.MicrosoftWelcome &&
          <RegStepMicrosoftWelcome orgName={microsoftOrg} toDashboard={toDashboard}/>}
      </div>
      <CancelRegistrationModal
        visible={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        onConfirm={cancelRegistration}
      />
    </div>
  );
}
